/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1779235200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed May 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "stackadapt",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to StackAdapt",
          "depth": 0,
          "id": "connect-to-stackadapt"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing segments",
          "depth": 1,
          "id": "syncing-segments"
        }, {
          "value": "Syncing device audiences",
          "depth": 1,
          "id": "syncing-device-audiences"
        }, {
          "value": "Handling PII and hashing",
          "depth": 0,
          "id": "handling-pii-and-hashing"
        }, {
          "value": "Syncing pixel events",
          "depth": 1,
          "id": "syncing-pixel-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {MatchBoosterDestination, EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-segments",
                children: _jsx(_components.strong, {
                  children: "CRM Segments"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to StackAdapt CRM segments"
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.stackadapt.com/graphql/scenarios/crm-audience-creation-with-pii",
                children: "Segments docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-device-audiences",
                children: _jsx(_components.strong, {
                  children: "Device Audience"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync mobile advertising IDs (MAID / IDFA / GAID) to StackAdapt device audiences"
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.stackadapt.com/graphql/scenarios/crm-audience-creation-with-pii",
                children: "Segments docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-pixel-events",
                children: _jsx(_components.strong, {
                  children: "Pixel API"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Pixel events"
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.stackadapt.com/pixel#http-method",
                children: "Pixel API docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-stackadapt",
        children: "Connect to StackAdapt"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "StackAdapt"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "StackAdapt"
        }), " by entering the following StackAdapt tokens:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pixel ID"
          }), ": (Only necessary for using Pixel API). If you don't have a Pixel ID, create one in StackAdapt."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "GraphQL API token"
          }), ": (Required for CRM segment and device audience syncs). If you don't have an API token, please reach out to your account manager."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your StackAdapt destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the StackAdapt destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-segments",
        children: "Syncing segments"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to StackAdapt CRM Segments"
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to segment in StackAdapt on any column in your model and any field in StackAdapt. Ensure the data types of the model column and StackAdapt field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.p, {
        children: "StackAdapt accepts the following fields: address, city, email, first name, last name, phone number, state, and zip code. For hashed PII data, you must select one of the following valid combinations of these identifiers:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "email"
        }), "\n", _jsx(_components.li, {
          children: "first name, last name, email"
        }), "\n", _jsx(_components.li, {
          children: "first name, last name, phone number"
        }), "\n", _jsx(_components.li, {
          children: "first name, last name, zip code"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not provide at least one of the possible combinations, StackAdapt will be unable to find matches. You can learn more here: ", _jsx(_components.a, {
          href: "https://support.stackadapt.com/hc/en-us/articles/360048698853-CRM-Audience-Creation-with-LiveRamp",
          children: "StackAdapt CRM Audience Creation"
        }), "."]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "StackAdapt"
      }), "\n", _jsx(_components.h3, {
        id: "syncing-device-audiences",
        children: "Syncing device audiences"
      }), "\n", _jsx(_components.p, {
        children: "Sync raw mobile advertising IDs (MAID / IDFA / GAID) to a StackAdapt device audience. StackAdapt does not hash device IDs, so the column you map must contain unhashed identifiers."
      }), "\n", _jsx(_components.h4, {
        id: "sync-mode",
        children: "Sync mode"
      }), "\n", _jsx(_components.p, {
        children: "Choose between sending records in batches via the GraphQL API or uploading a file via a pre-signed URL:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Inline API (Default)"
          }), ": sends added rows from your query results in each run via the GraphQL API."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "File Upload API"
          }), ": uploads the device IDs as a file via a pre-signed URL. Recommended for very large audiences."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "device-id-column",
        children: "Device ID column"
      }), "\n", _jsxs(_components.p, {
        children: ["Map a single model column to ", _jsx(_components.strong, {
          children: "Device ID (MAID / IDFA / GAID)"
        }), ". Rows with missing, empty, or non-string values in this column are skipped and surfaced in the ", _jsx(_components.a, {
          href: "#live-debugger",
          children: "Live debugger"
        }), " as rejected rows."]
      }), "\n", _jsx(_components.h4, {
        id: "cross-device-targeting",
        children: "Cross-device targeting"
      }), "\n", _jsxs(_components.p, {
        children: ["Optionally enable ", _jsx(_components.strong, {
          children: "Cross-device targeting"
        }), " so StackAdapt extends the audience across related devices for the same user."]
      }), "\n", _jsx(_components.h4, {
        id: "advanced",
        children: "Advanced"
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Advanced"
        }), ", you can override the default batch size. The default is 1,000 rows per batch and the maximum is 10,000. Decreasing the batch size can help if you hit request-size errors."]
      }), "\n", _jsx(_components.h2, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsx(_components.p, {
        children: "You can sync data to StackAdapt in one of two formats: either as a list of strings containing hashed PII data or as a list of objects containing users' information."
      }), "\n", _jsxs(_components.p, {
        children: ["Currently, StackAdapt only supports SHA_1 as the hashing algorithm. For more information, refer to the ", _jsx(_components.a, {
          href: "https://docs.stackadapt.com/graphql/scenarios/crm-audience-creation-with-pii#option-2-use-hashed-pii-data-and-specify-the-hashing-algorithm",
          children: "StackAdapt documentation on hashed PII data"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-pixel-events",
        children: "Syncing pixel events"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to StackAdapt pixel events"
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you map model columns to event properties. The required event properties are:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "IP address"
        }), "\n", _jsx(_components.li, {
          children: "Page title"
        }), "\n", _jsx(_components.li, {
          children: "User agent"
        }), "\n", _jsx(_components.li, {
          children: "URL of target website"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "event-args",
        children: "Event args"
      }), "\n", _jsx(_components.p, {
        children: "You can also map additional event data that Hightouch sends to StackAdapt via the args property which can be used for pixel rule matching."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
